import React, { useCallback, useState, useEffect } from "react";
import { Container, Row, Col, Spinner } from "reactstrap";
import { API_URL } from "../../config";

const WalletAddress = () => {
  const [addressData, setAddressData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedCurrency, setSelectedCurrency] = useState("");
  const [currencies, setCurrencies] = useState([]);

  const [page, setPage] = useState(1);
  const limit = 10;
  const [totalPages, setTotalPages] = useState(1);
  const [offset, setOffset] = useState(0);

  const fetchAddressData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const bodyPayload = {
        currencyName: "",
        sortOption: "-1",
      };

      const requestBody = {
        formValue: bodyPayload,
        limit,
        offset,
      };

      const response = await fetch(API_URL + "/api/wallet/getWallet/address", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.status) {
        const addressList = data.data?.address || [];
        setAddressData(addressList);

        const totalRecords = data.data?.total || addressList.length || 0;
        setTotalPages(Math.ceil(totalRecords / limit));
        const uniqueCurrencies = [
          ...new Set(addressList.map((item) => item.currencyname)),
        ];
        setCurrencies(uniqueCurrencies);
      } else {
        throw new Error(data.message || "Failed to fetch data");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedCurrency, offset]);

  useEffect(() => {
    fetchAddressData();
  }, [fetchAddressData]);

  const handleCurrencyChange = (e) => {
    setSelectedCurrency(e.target.value);
    setPage(1);
    setOffset(0);
  };

  const handleDownloadCSV = () => {
    const csvRows = [
      ["User", "Network", "Address"],
      ...addressData.map((item) => [
        item?.user_id?.email || "",
        item?.currencyname || "",
        item?.address || "",
      ]),
    ];

    const csvContent = csvRows.map((row) => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.setAttribute("hidden", "");
    a.setAttribute("href", url);
    a.setAttribute(
      "download",
      `wallet_addresses_${selectedCurrency || "all"}.csv`
    );
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setOffset((newPage - 1) * limit);
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col>
            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
              <h4 className="mb-sm-0 font-size-18">Wallet Address</h4>
            </div>
          </Col>
        </Row>

        <div className="d-space-between mb-3 arrow-need">
          <select
            value={selectedCurrency}
            onChange={handleCurrencyChange}
            className="form-control w-auto"
          >
            <option value="">All Currencies</option>
            {currencies.map((currency) => (
              <option key={currency} value={currency}>
                {currency}
              </option>
            ))}
          </select>

          <div className="r-g-3">
            <button
              className="btn btn-primary btn-sm"
              onClick={handleDownloadCSV}
            >
              Export CSV
            </button>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => {
                setSelectedCurrency("");
                setPage(1);
                setOffset(0);
                fetchAddressData();
              }}
            >
              Refresh
            </button>
          </div>
        </div>

        {loading ? (
          <Spinner />
        ) : error ? (
          <div className="text-danger">{error}</div>
        ) : (
          <>
            <table className="table text-center">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Network</th>
                  <th>Address</th>
                </tr>
              </thead>
              <tbody>
                {addressData.length > 0 ? (
                  addressData.map((address) => (
                    <tr key={address?._id}>
                      <td>{address?.user_id?.email}</td>
                      <td>{address?.currencyname}</td>
                      <td>{address?.address}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3">No records found</td>
                  </tr>
                )}
              </tbody>
            </table>

            <div className="d-flex justify-content-center align-items-center mt-3">
              <button
                disabled={page === 1}
                onClick={() => handlePageChange(page - 1)}
                className="btn btn-primary mx-2"
              >
                Prev
              </button>
              <span>
                Page {page} of {totalPages}
              </span>
              <button
                disabled={page === totalPages}
                onClick={() => handlePageChange(page + 1)}
                className="btn btn-primary mx-2"
              >
                Next
              </button>
            </div>
          </>
        )}
      </Container>
    </div>
  );
};

export default WalletAddress;
